import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, Tags, AlertTriangle } from 'lucide-react';
import { findShadowedRules } from './ruleIntel';
import { PageHeader } from '@/components/common/PageHeader';
import { DataTable, type Column } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { StatusBadge } from '@/components/common/StatusBadge';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import {
  MultiSelect,
  type MultiSelectOption,
} from '@/components/common/MultiSelect';
import { COUNTRIES, INDIA_STATES } from '@/lib/geography';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FormSheet } from '@/components/common/FormSheet';
import { FormField } from '@/components/common/FormField';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  dispatchService,
  dispatchRulesService,
  type DispatchAssignmentRule,
} from '@/services/dispatch';
import { platformWarehousesService } from '@/services/platform/warehouses';
import type { DispatchPartner } from '@/types/dispatch';

/**
 * D-2 — assignment-rules CRUD. Priority-ordered match-and-route
 * rules the dispatch automation evaluates on every payment confirmation.
 *
 * Engine semantics (from Bundle A Sh2a):
 *   - Lower priority number wins.
 *   - Conditions are AND — every non-empty matcher must match.
 *   - Empty matcher list = wildcard (matches anything).
 *   - First match returns; if no rule matches, dispatch lands with
 *     partnerId=NULL and operator picks manually.
 */
export function DispatchRulesPage({ embedded = false }: { embedded?: boolean } = {}) {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<DispatchAssignmentRule | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<DispatchAssignmentRule | null>(null);

  const rulesQuery = useQuery({
    queryKey: ['dispatch', 'rules'],
    queryFn: () => dispatchRulesService.list(),
  });
  const partnersQuery = useQuery({
    queryKey: ['dispatch', 'partners'],
    queryFn: () => dispatchService.listPartners(),
  });

  const partnersById = useMemo(() => {
    const map = new Map<string, DispatchPartner>();
    for (const p of partnersQuery.data ?? []) map.set(p.id, p);
    return map;
  }, [partnersQuery.data]);

  // Misconfiguration intelligence — rules made unreachable by a broader,
  // higher-priority rule above them (they will never fire).
  const shadowed = useMemo(
    () => findShadowedRules(rulesQuery.data ?? []),
    [rulesQuery.data],
  );

  const movePriority = useMutation({
    mutationFn: ({ id, delta }: { id: string; delta: number }) => {
      const rule = (rulesQuery.data ?? []).find((r) => r.id === id);
      if (!rule) throw new Error('Rule not found');
      return dispatchRulesService.update(id, { priority: rule.priority + delta });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dispatch', 'rules'] }),
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Reorder failed'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => dispatchRulesService.remove(id),
    onSuccess: () => {
      toast.success('Rule removed');
      qc.invalidateQueries({ queryKey: ['dispatch', 'rules'] });
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not remove rule'),
  });

  const columns: Column<DispatchAssignmentRule>[] = [
    {
      key: 'priority',
      header: 'Priority',
      cell: (r) => (
        <div className="flex items-center gap-1">
          <Badge variant="muted" className="min-w-[36px] justify-center font-mono">
            {r.priority}
          </Badge>
          <Button
            size="icon"
            variant="ghost"
            className="h-6 w-6 text-muted-foreground"
            onClick={() => movePriority.mutate({ id: r.id, delta: -10 })}
            aria-label="Higher priority"
            title="Higher priority (lower number)"
          >
            <ArrowUp className="h-3 w-3" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-6 w-6 text-muted-foreground"
            onClick={() => movePriority.mutate({ id: r.id, delta: 10 })}
            aria-label="Lower priority"
            title="Lower priority (higher number)"
          >
            <ArrowDown className="h-3 w-3" />
          </Button>
        </div>
      ),
    },
    {
      key: 'name',
      header: 'Rule',
      cell: (r) => {
        const by = shadowed.get(r.id);
        return (
          <div className="space-y-0.5">
            <div className="font-medium text-foreground">{r.name}</div>
            {by && (
              <div className="flex items-center gap-1 text-[10.5px] text-destructive">
                <AlertTriangle className="h-3 w-3 shrink-0" />
                Never fires — shadowed by #{by.priority} “{by.name}”
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: 'conditions',
      header: 'When',
      cell: (r) => {
        const chips: string[] = [];
        if (r.countryIn.length) chips.push(`Country: ${r.countryIn.join(', ')}`);
        if (r.stateIn.length) chips.push(`State: ${r.stateIn.join(', ')}`);
        if (r.warehouseCodeIn.length) chips.push(`Warehouse: ${r.warehouseCodeIn.join(', ')}`);
        if (chips.length === 0) return <span className="text-xs italic text-muted-foreground">Any (wildcard)</span>;
        return (
          <div className="flex flex-wrap gap-1">
            {chips.map((c) => (
              <Badge key={c} variant="muted" className="text-[10px]">{c}</Badge>
            ))}
          </div>
        );
      },
    },
    {
      key: 'scope',
      header: 'Destination',
      width: '120px',
      cell: (r) => (
        <Badge
          variant={r.scope === 'ALL' ? 'muted' : 'success'}
          className="text-[10px]"
        >
          {r.scope === 'DOMESTIC'
            ? 'Domestic'
            : r.scope === 'INTERNATIONAL'
              ? 'International'
              : 'All'}
        </Badge>
      ),
    },
    {
      key: 'tier',
      header: 'Tier',
      width: '110px',
      cell: (r) => (
        <Badge variant={r.tier ? 'success' : 'muted'} className="text-[10px]">
          {r.tier === 'STANDARD' ? 'Standard' : r.tier === 'EXPRESS' ? 'Express' : 'Any'}
        </Badge>
      ),
    },
    {
      key: 'partner',
      header: 'Then assign',
      cell: (r) => {
        const p = partnersById.get(r.partnerId);
        if (!p) return <span className="text-xs text-destructive">(partner missing)</span>;
        const service = r.serviceCode
          ? (p.enabledServices ?? []).find((s) => s.code === r.serviceCode)
          : null;
        return (
          <div className="space-y-0.5 text-sm">
            <div>
              <span className="font-mono text-xs text-muted-foreground">{p.code}</span>
              {' · '}
              {p.name}
            </div>
            {r.serviceCode ? (
              <div className="text-[11px]">
                <span className="text-muted-foreground">via </span>
                <span className="font-mono">{r.serviceCode}</span>
                {service?.label && (
                  <span className="text-muted-foreground"> · {service.label}</span>
                )}
              </div>
            ) : (
              <div className="text-[11px] italic text-muted-foreground">via partner default</div>
            )}
          </div>
        );
      },
    },
    { key: 'status', header: 'Status', cell: (r) => <StatusBadge status={r.status} /> },
    {
      key: 'actions',
      header: '',
      align: 'right',
      cell: (r) => (
        <div className="-mr-2 flex justify-end gap-0.5">
          <Button variant="ghost" size="sm" onClick={() => setEditing(r)} aria-label={`Edit ${r.name}`}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setConfirmRemove(r)}
            className="text-muted-foreground hover:text-destructive"
            aria-label={`Remove ${r.name}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ),
    },
  ];

  const newRuleButton = (
    <Button size="sm" onClick={() => setCreateOpen(true)} disabled={(partnersQuery.data ?? []).length === 0}>
      <Plus className="h-3.5 w-3.5" />
      New rule
    </Button>
  );

  return (
    <div className="space-y-6">
      {embedded ? (
        <div className="flex flex-wrap items-start justify-between gap-3">
          <p className="max-w-2xl text-[11.5px] text-muted-foreground">
            Auto-assign delivery partners by destination. Lower priority wins; conditions are AND
            (every non-empty matcher must match). A wildcard rule (no conditions) at the highest
            priority number acts as the default.
          </p>
          {newRuleButton}
        </div>
      ) : (
        <PageHeader
          eyebrow="Fulfilment"
          title="Routing Rules"
          description="Auto-assign delivery partners by destination. Lower priority wins; conditions are AND (every non-empty matcher must match). Wildcard rule (no conditions) at the highest priority number acts as the default."
          actions={newRuleButton}
        />
      )}

      {(partnersQuery.data ?? []).length === 0 && (
        <div className="rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-[12.5px] text-warning-foreground">
          <strong>Add a delivery partner first</strong> — rules route to partners. <a href="/dispatch/partners" className="underline">Open Partners →</a>
        </div>
      )}

      {shadowed.size > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/[0.06] px-3 py-2 text-[12.5px]">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
          <div className="text-foreground">
            <strong>
              {shadowed.size} rule{shadowed.size === 1 ? '' : 's'} never fire
              {shadowed.size === 1 ? 's' : ''}
            </strong>{' '}
            — a broader rule above {shadowed.size === 1 ? 'it' : 'them'} already matches every
            shipment {shadowed.size === 1 ? 'it' : 'they'} would. Shipments will route to the
            broader rule&rsquo;s partner instead. Narrow the higher rule&rsquo;s destination/scope,
            or move the specific rule above it.
          </div>
        </div>
      )}

      {rulesQuery.error ? (
        <ErrorState error={rulesQuery.error} onRetry={() => rulesQuery.refetch()} />
      ) : (
        <DataTable
          columns={columns}
          rows={rulesQuery.data ?? []}
          rowKey={(r) => r.id}
          isLoading={rulesQuery.isLoading}
          emptyTitle="No rules yet"
          emptyDescription="Add a rule so the dispatch automation knows which partner to assign. Start with a wildcard default."
          emptyAction={
            <Button size="sm" onClick={() => setCreateOpen(true)} disabled={(partnersQuery.data ?? []).length === 0}>
              <Tags className="h-3.5 w-3.5" />
              New rule
            </Button>
          }
        />
      )}

      <RuleFormSheet
        open={createOpen}
        onOpenChange={setCreateOpen}
        partners={partnersQuery.data ?? []}
      />
      <RuleFormSheet
        open={!!editing}
        onOpenChange={(o) => !o && setEditing(null)}
        partners={partnersQuery.data ?? []}
        rule={editing}
      />
      <ConfirmDialog
        open={!!confirmRemove}
        onOpenChange={(o) => !o && setConfirmRemove(null)}
        title={confirmRemove ? `Remove rule "${confirmRemove.name}"?` : ''}
        description={
          <>
            The dispatch automation will no longer apply this rule. In-flight dispatches stay
            assigned; future ones fall through to the next matching rule (or land unassigned).
          </>
        }
        confirmLabel="Remove rule"
        onConfirm={async () => {
          if (confirmRemove) await remove.mutateAsync(confirmRemove.id);
        }}
      />
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────

export function RuleFormSheet({
  open,
  onOpenChange,
  partners,
  rule,
  defaultTier,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  partners: DispatchPartner[];
  rule?: DispatchAssignmentRule | null;
  /** Pre-scope a NEW rule to a tier (e.g. when adding from the Dispatcher). */
  defaultTier?: 'STANDARD' | 'EXPRESS';
}) {
  const qc = useQueryClient();
  const isEdit = !!rule;
  // Sh-rebuild-E — service picker uses this sentinel to mean
  // "use the partner's adapter default". Empty string can't round-
  // trip through shadcn Select so we use a distinct token; the
  // mutationFn maps it back to null when sending to the BE.
  const SERVICE_DEFAULT = '__partner_default__';

  const seed = () => ({
    name: rule?.name ?? '',
    priority: rule?.priority ?? 100,
    status: (rule?.status ?? 'ACTIVE') as 'ACTIVE' | 'INACTIVE',
    scope: (rule?.scope ?? 'ALL') as 'ALL' | 'DOMESTIC' | 'INTERNATIONAL',
    countryIn: rule?.countryIn ?? [],
    stateIn: rule?.stateIn ?? [],
    warehouseCodeIn: rule?.warehouseCodeIn ?? [],
    tier: (rule?.tier ?? defaultTier ?? 'ANY') as 'ANY' | 'STANDARD' | 'EXPRESS',
    partnerId: rule?.partnerId ?? (partners[0]?.id ?? ''),
    serviceCode: rule?.serviceCode ?? SERVICE_DEFAULT,
  });
  const [form, setForm] = useState(seed);

  // Re-seed on open + on rule prop change. The edit sheet is a
  // sibling instance to the create sheet (both mounted), so without
  // this useEffect the form captures `rule=null` on first mount and
  // never picks up the actual record when the operator clicks Edit.
  useEffect(() => {
    if (!open) return;
    setForm(seed());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, rule?.id, partners.length]);

  // Tenant warehouses for the warehouse multi-select. The dispatch
  // partners list query already runs on this page; we add a parallel
  // warehouses query here. Scoped to the operator's tenant via the
  // PLATFORM_ADMIN+TENANT_ADMIN-gated platform/warehouses endpoint.
  const warehousesQuery = useQuery({
    queryKey: ['dispatch-rules', 'warehouses'],
    queryFn: () => platformWarehousesService.list({ pageSize: 200 }),
    enabled: open,
  });
  const warehouseOptions: MultiSelectOption[] = useMemo(() => {
    const rows = warehousesQuery.data?.items ?? [];
    return rows.map((w) => ({
      value: w.code,
      label: w.name,
      hint: w.address?.city,
    }));
  }, [warehousesQuery.data]);
  const countryOptions: MultiSelectOption[] = useMemo(
    () => COUNTRIES.map((c) => ({ value: c.code, label: c.name })),
    [],
  );
  const stateOptions: MultiSelectOption[] = useMemo(
    () =>
      INDIA_STATES.map((s) => ({
        value: s.code,
        label: s.name,
        badge: s.kind === 'ut' ? 'UT' : undefined,
      })),
    [],
  );

  // When the operator switches partner, drop the previously-selected
  // service if it's no longer in the new partner's enabledServices.
  const selectedPartner = partners.find((p) => p.id === form.partnerId) ?? null;
  const partnerServices = selectedPartner?.enabledServices ?? [];

  const mutation = useMutation({
    mutationFn: async () => {
      const isDefault = form.serviceCode === SERVICE_DEFAULT;
      const payload = {
        name: form.name.trim(),
        priority: Number(form.priority) || 100,
        status: form.status,
        scope: form.scope,
        // All three filter fields are picker-driven now — no
        // splitCsv / normalisation needed; the operator can't enter
        // garbage like "MA" (Maharashtra-typo) any more.
        countryIn: form.countryIn,
        stateIn: form.stateIn,
        warehouseCodeIn: form.warehouseCodeIn,
        tier: form.tier === 'ANY' ? null : form.tier,
        partnerId: form.partnerId,
        // Empty string explicitly clears on update; on create we
        // omit to leave NULL. Either way the BE persists null.
        serviceCode: isDefault ? (isEdit ? '' : undefined) : form.serviceCode,
      };
      return isEdit && rule
        ? dispatchRulesService.update(rule.id, payload)
        : dispatchRulesService.create(payload);
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Rule updated' : 'Rule created');
      qc.invalidateQueries({ queryKey: ['dispatch', 'rules'] });
      onOpenChange(false);
    },
  });

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? `Edit "${rule?.name}"` : 'New routing rule'}
      description="Conditions are AND. Empty = wildcard. Lower priority wins."
      submitLabel={isEdit ? 'Save rule' : 'Create rule'}
      onSubmit={(e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.partnerId) return;
        mutation.mutate();
      }}
      isSubmitting={mutation.isPending}
      error={mutation.error}
      // Lock height so the conditions list + partner/service selects
      // + footer all stay reachable on shorter screens. Without this
      // the dialog hugs content and overflows past the viewport.
      fixedHeight="lg"
    >
      <FormField label="Rule name" htmlFor="name" required>
        <Input
          id="name"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          placeholder="e.g. Default → BlueDart, MH metros → Delhivery"
          required
          maxLength={200}
        />
      </FormField>
      <div className="grid grid-cols-3 gap-4">
        <FormField label="Priority" htmlFor="priority" hint="Lower wins. Default 100.">
          <Input
            id="priority"
            type="number"
            value={form.priority}
            onChange={(e) => setForm((f) => ({ ...f, priority: Number(e.target.value) }))}
            min={0}
            max={10000}
          />
        </FormField>
        <FormField label="Status" htmlFor="status">
          <Select
            value={form.status}
            onValueChange={(v) => setForm((f) => ({ ...f, status: v as 'ACTIVE' | 'INACTIVE' }))}
          >
            <SelectTrigger id="status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ACTIVE">ACTIVE</SelectItem>
              <SelectItem value="INACTIVE">INACTIVE</SelectItem>
            </SelectContent>
          </Select>
        </FormField>
        <FormField
          label="Destination"
          htmlFor="scope"
          hint="Domestic = your home country · International = abroad · All = either."
        >
          <Select
            value={form.scope}
            onValueChange={(v) =>
              setForm((f) => ({ ...f, scope: v as 'ALL' | 'DOMESTIC' | 'INTERNATIONAL' }))
            }
          >
            <SelectTrigger id="scope"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All destinations</SelectItem>
              <SelectItem value="DOMESTIC">Domestic (within home country)</SelectItem>
              <SelectItem value="INTERNATIONAL">International (abroad)</SelectItem>
            </SelectContent>
          </Select>
        </FormField>
        <FormField
          label="Tier"
          htmlFor="tier"
          hint="Scope this rule to a delivery tier, or leave Any."
        >
          <Select
            value={form.tier}
            onValueChange={(v) =>
              setForm((f) => ({ ...f, tier: v as 'ANY' | 'STANDARD' | 'EXPRESS' }))
            }
          >
            <SelectTrigger id="tier"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ANY">Any tier</SelectItem>
              <SelectItem value="STANDARD">Standard only</SelectItem>
              <SelectItem value="EXPRESS">Express only</SelectItem>
            </SelectContent>
          </Select>
        </FormField>
        <FormField label="Assign to" htmlFor="partner" required>
          <Select
            value={form.partnerId}
            onValueChange={(v) => {
              // Drop serviceCode if the new partner doesn't expose it.
              const next = partners.find((p) => p.id === v);
              const nextCodes = (next?.enabledServices ?? []).map((s) => s.code);
              setForm((f) => ({
                ...f,
                partnerId: v,
                serviceCode:
                  f.serviceCode === SERVICE_DEFAULT || nextCodes.includes(f.serviceCode)
                    ? f.serviceCode
                    : SERVICE_DEFAULT,
              }));
            }}
          >
            <SelectTrigger id="partner"><SelectValue placeholder="Pick partner" /></SelectTrigger>
            <SelectContent>
              {partners.filter((p) => p.status === 'ACTIVE').map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  <span className="font-mono text-[12px]">{p.code}</span>
                  <span className="ml-2 text-[11px] text-muted-foreground">{p.name}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
      </div>
      <FormField
        label="Service"
        htmlFor="service"
        hint={
          partnerServices.length === 0
            ? 'This partner exposes no services (MANUAL partner or no services configured yet) — adapter picks its default.'
            : 'Which service inside the partner to dispatch via. Default = adapter chooses.'
        }
      >
        <Select
          value={form.serviceCode}
          onValueChange={(v) => setForm((f) => ({ ...f, serviceCode: v }))}
          disabled={partnerServices.length === 0}
        >
          <SelectTrigger id="service"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={SERVICE_DEFAULT}>
              <span className="text-[12px] italic text-muted-foreground">Use partner default</span>
            </SelectItem>
            {partnerServices.map((s) => (
              <SelectItem key={s.code} value={s.code}>
                <span className="font-mono text-[12px]">{s.code}</span>
                <span className="ml-2 text-[11px] text-muted-foreground">{s.label}</span>
                {s.isCod && (
                  <Badge variant="muted" className="ml-2 text-[9.5px]">COD</Badge>
                )}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormField>
      <div className="rounded-md border border-info/30 bg-info/5 px-3 py-2 text-[11.5px] text-muted-foreground">
        <strong className="text-foreground">Conditions</strong> — pick one or
        more from each list. Leave empty to wildcard. Conditions AND together,
        so a rule with State=KA + Warehouse=WH-MUM matches only Karnataka
        orders shipping out of Mumbai. First rule (lowest priority) wins.
      </div>
      <FormField
        label="Country"
        htmlFor="countryIn"
        hint="Empty = any country. Pick destinations this rule should route."
      >
        <MultiSelect
          options={countryOptions}
          value={form.countryIn}
          onChange={(v) => setForm((f) => ({ ...f, countryIn: v }))}
          placeholder="Any country"
        />
      </FormField>
      <FormField
        label="State / Region"
        htmlFor="stateIn"
        hint="India states + UTs. Empty = any state. Codes match GSTIN; legacy free-text values like 'ASSAM' are also matched by the BE."
      >
        <MultiSelect
          options={stateOptions}
          value={form.stateIn}
          onChange={(v) => setForm((f) => ({ ...f, stateIn: v }))}
          placeholder="Any state"
        />
      </FormField>
      <FormField
        label="Warehouse"
        htmlFor="warehouseCodeIn"
        hint="Pickup-side warehouse. Empty = any."
      >
        <MultiSelect
          options={warehouseOptions}
          value={form.warehouseCodeIn}
          onChange={(v) => setForm((f) => ({ ...f, warehouseCodeIn: v }))}
          placeholder="Any warehouse"
          emptyMessage={
            warehousesQuery.isLoading
              ? 'Loading warehouses…'
              : 'No warehouses defined yet — add one in Workspace → Warehouses.'
          }
        />
      </FormField>
    </FormSheet>
  );
}
